import React, { useState, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faReceipt,
  faMoneyBill,
  faFilter,
  faEye,
  faSpinner,
  faCheckCircle,
  faClose,
  faBalanceScale,
  faWarehouse,
  faBoxes,
  faDollarSign,
  faTags,
  faShoppingCart,
  faUsers,
  faTruck,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
// eslint-disable-next-line no-unused-vars
import useSWR, { mutate } from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Authorization: authHeader,
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function DashboardContent() {
  const { stateNavigator } = useNavigationEvent();

  const userId = ls.get("currentuserid");

  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // SWR Data fetching for various data sources
  const { data: salessales, mutate: mutatesales } = useSWR(
    `${process.env.REACT_APP_SALES_ORDER_API}?include=field_cashier,field_customer&sort=-created&page[limit]=1000`,
    fetcher,
  );
  const salessliced = salessales?.data
    ? salessales?.data.slice(0, 5)
    : salessales;
  const { data: customers } = useSWR(
    `${process.env.REACT_APP_CUSTOMER_API}?fields[node--customer]=title,field_customer_email,field_phone_number,field_customer_address,drupal_internal__nid&page[limit]=1000`,
    fetcher,
  );

  const { data: users } = useSWR(
    `${process.env.REACT_APP_USER_API}?&filter[drupal_internal__uid]=${userId}`,
    fetcher,
  );

  const { data: products } = useSWR(
    `${process.env.REACT_APP_ADD_PRODUCT_API}?fields[node--product]=title,field_sku,field_stock_quantity,field_price,field_cost_price&page[limit]=1000`,
    fetcher,
  );

  const { data: stockMovements } = useSWR(
    `${process.env.REACT_APP_STOCK_MOVEMENT_API}?include=field_product,field_product_supplier&fields[node--stock_movement]=title,field_quantity,field_stock_movement_cost_price,field_markup,field_selling_price,field_invoice_number,field_date_received,field_movement_type&page[limit]=1000&sort=-created`,
    fetcher,
  );

  // Calculate comprehensive statistics
  const statistics = useMemo(() => {
    // Sales Statistics
    let salesStats = {
      totalRevenue: 0,
      totalSalesCount: 0,
      averageSaleAmount: 0,
      totalBalanceDue: 0,
      totalAmountPaid: 0,
      todayRevenue: 0,
      todaySalesCount: 0,
    };

    if (salessales?.data) {
      const today = new Date().toISOString().split("T")[0];
      salessales.data.forEach((sale) => {
        const amount = parseFloat(sale.attributes.field_total_amount || 0);
        const paid = parseFloat(sale.attributes.field_total_paid || 0);
        const saleDate = new Date(sale.attributes.created)
          .toISOString()
          .split("T")[0];

        salesStats.totalRevenue += amount;
        salesStats.totalAmountPaid += paid;

        if (saleDate === today) {
          salesStats.todayRevenue += amount;
          salesStats.todaySalesCount++;
        }
      });

      salesStats.totalSalesCount = salessales.data.length;
      salesStats.averageSaleAmount =
        salesStats.totalSalesCount > 0
          ? salesStats.totalRevenue / salesStats.totalSalesCount
          : 0;
      salesStats.totalBalanceDue =
        salesStats.totalRevenue - salesStats.totalAmountPaid;
    }

    // Inventory Statistics
    let inventoryStats = {
      totalProducts: 0,
      totalStockValue: 0,
      totalStockQuantity: 0,
      totalCostValue: 0,
      lowStockItems: 0,
      outOfStockItems: 0,
      inventoryTurnover: 0,
    };

    if (products?.data) {
      let totalCost = 0;
      products.data.forEach((product) => {
        const stock = parseInt(product.attributes.field_stock_quantity) || 0;
        const price = parseFloat(product.attributes.field_price) || 0;
        const cost = parseFloat(product.attributes.field_cost_price) || 0;

        inventoryStats.totalProducts++;
        inventoryStats.totalStockQuantity += stock;
        inventoryStats.totalStockValue += stock * price;
        totalCost += stock * cost;

        // Check stock status
        if (stock === 0) {
          inventoryStats.outOfStockItems++;
        } else if (stock <= 10) {
          // Define low stock threshold
          inventoryStats.lowStockItems++;
        }
      });

      inventoryStats.totalCostValue = totalCost;

      // Calculate inventory turnover (simplified)
      if (inventoryStats.totalCostValue > 0 && salesStats.totalRevenue > 0) {
        inventoryStats.inventoryTurnover = (
          salesStats.totalRevenue / inventoryStats.totalCostValue
        ).toFixed(2);
      }
    }

    // Stock Movement Statistics
    let stockMovementStats = {
      totalReceived: 0,
      totalIssued: 0,
      recentReceipts: 0,
      recentIssues: 0,
      stockValueChange: 0,
    };

    if (stockMovements?.data) {
      const today = new Date();
      const last7Days = new Date(today);
      last7Days.setDate(today.getDate() - 7);

      stockMovements.data.forEach((movement) => {
        const quantity = parseInt(movement.attributes.field_quantity) || 0;
        const costPrice =
          parseFloat(movement.attributes.field_stock_movement_cost_price) || 0;
        const movementDate = new Date(
          movement.attributes.field_date_received ||
            movement.attributes.created,
        );
        const isRecent = movementDate >= last7Days;

        if (movement.attributes.field_movement_type === "received") {
          stockMovementStats.totalReceived += quantity;
          if (isRecent) stockMovementStats.recentReceipts += quantity;
          stockMovementStats.stockValueChange += quantity * costPrice;
        } else if (movement.attributes.field_movement_type === "issue") {
          stockMovementStats.totalIssued += quantity;
          if (isRecent) stockMovementStats.recentIssues += quantity;
          stockMovementStats.stockValueChange -= quantity * costPrice;
        }
      });
    }

    // Customer Statistics
    let customerStats = {
      totalCustomers: 0,
      activeCustomers: 0,
      newCustomersThisMonth: 0,
    };

    if (customers?.data) {
      customerStats.totalCustomers = customers.data.length;
      // For active customers, we could check customers with recent purchases
      // For now, just count total
      customerStats.activeCustomers = customers.data.length;

      customerStats.newCustomersThisMonth = customers.data.length; // Simplified
    }

    return {
      sales: salesStats,
      inventory: inventoryStats,
      stockMovements: stockMovementStats,
      customers: customerStats,
      // Combined metrics
      profitMargin:
        salesStats.totalRevenue > 0
          ? (
              ((salesStats.totalRevenue - inventoryStats.totalCostValue) /
                salesStats.totalRevenue) *
              100
            ).toFixed(1)
          : 0,
      stockCoverage:
        salesStats.averageSaleAmount > 0
          ? (
              inventoryStats.totalStockValue / salesStats.averageSaleAmount
            ).toFixed(1)
          : 0,
    };
  }, [salessales, products, stockMovements, customers]);

  const {
    sales,
    inventory,
    stockMovements: stockMovementsStats,
    customers: customerStats,
  } = statistics;

  // Helper function to calculate balance for a single sale
  const calculateSaleBalance = (sale) => {
    const total = parseFloat(sale.attributes.field_total_amount || 0);
    const paid = parseFloat(sale.attributes.field_total_paid || 0);
    return total - paid;
  };

  // Get balance display with formatting and styling
  const getBalanceDisplay = (sale) => {
    const balance = calculateSaleBalance(sale);

    if (balance > 0) {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#d32f2f",
        status: "Due",
      };
    } else if (balance < 0) {
      return {
        formatted: `GH₵${Math.abs(balance).toFixed(2)}`,
        color: "#ff9800",
        status: "Credit",
      };
    } else {
      return {
        formatted: `GH₵${balance.toFixed(2)}`,
        color: "#388e3c",
        status: "Paid",
      };
    }
  };

  const handleDelete = async (salesId) => {
    try {
      const freshToken = await getCsrfToken();

      if (!freshToken) {
        setErrorMessage("Unable to authenticate. Please refresh the page.");
        setModalError(true);
        return;
      }

      if (window.confirm("Are you sure you want to delete this sale?")) {
        const res = await fetch(
          `${process.env.REACT_APP_SALES_ORDER_API}/${salesId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: authHeader,
              "Content-Type": "application/vnd.api+json",
              "X-CSRF-Token": freshToken,
            },
            credentials: "include",
          },
        );

        if (res.ok) {
          setModalSuccessMsg(true);
          mutatesales();
        } else {
          setErrorMessage("Failed to delete sale");
          setModalError(true);
        }
      }
    } catch (error) {
      setErrorMessage("Network error");
      setModalError(true);
    }
  };

  const getCashierName = (sale) => {
    const cashierId = sale.relationships?.field_cashier?.data?.id;
    if (!cashierId || !users?.data) return "N/A";
    const user = users.data.find((u) => u.id === cashierId);
    return user?.attributes?.name || "N/A";
  };

  const getCustomerName = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.title || "N/A";
  };

  const getCustomerPhoneNumber = (sale) => {
    const customerId = sale.relationships?.field_customer?.data?.id;
    if (!customerId || !customers?.data) return "N/A";
    const customer = customers.data.find((c) => c.id === customerId);
    return customer?.attributes?.field_phone_number || "N/A";
  };

  // Quick action buttons
  const quickActions = [
    {
      label: "Receive Stock",
      icon: faTruck,
      onClick: () => stateNavigator.navigate("receivestock"),
      color: "#4caf50",
    },
    {
      label: "Add Product",
      icon: faBoxes,
      onClick: () => stateNavigator.navigate("addproduct"),
      color: "#2196f3",
    },
    {
      label: "Add Customer",
      icon: faUsers,
      onClick: () => stateNavigator.navigate("addcustomer"),
      color: "#9c27b0",
    },
    {
      label: "View Inventory",
      icon: faWarehouse,
      onClick: () => stateNavigator.navigate("stockinventory"),
      color: "#ff9800",
    },
  ];

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="dashboard-main" style={styles.container}>
          <TopActionMenus
            AddTitle="Dashboard"
            buttonLabel="New Sale"
            onClick={() => stateNavigator.navigate("salesorder")}
          />

          {/* COMPREHENSIVE STATISTICS GRID */}
          <div style={styles.statisticsGrid}>
            {/* SALES STATISTICS */}
            <div style={styles.statSection}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faShoppingCart} /> Sales Overview
              </h3>
              <div style={styles.statCards}>
                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon icon={faReceipt} style={styles.statIcon} />
                    <span style={styles.statCardTitle}>Total Sales</span>
                  </div>
                  <div style={styles.statCardValue}>
                    {sales.totalSalesCount}
                  </div>
                  <div style={styles.statCardSubtext}>
                    Today: {sales.todaySalesCount}
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon
                      icon={faMoneyBill}
                      style={styles.statIcon}
                    />
                    <span style={styles.statCardTitle}>Revenue</span>
                  </div>
                  <div style={styles.statCardValue}>
                    GH₵{sales.totalRevenue.toFixed(2)}
                  </div>
                  <div style={styles.statCardSubtext}>
                    Today: GH₵{sales.todayRevenue.toFixed(2)}
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon
                      icon={faBalanceScale}
                      style={styles.statIcon}
                    />
                    <span style={styles.statCardTitle}>Balance Due</span>
                  </div>
                  <div style={styles.statCardValue}>
                    GH₵{sales.totalBalanceDue.toFixed(2)}
                  </div>
                  <div style={styles.statCardSubtext}>
                    {(
                      (sales.totalBalanceDue / sales.totalRevenue) * 100 || 0
                    ).toFixed(1)}
                    % of revenue
                  </div>
                </div>
              </div>
            </div>

            {/* INVENTORY STATISTICS */}
            <div style={styles.statSection}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faWarehouse} /> Inventory Overview
              </h3>
              <div style={styles.statCards}>
                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon icon={faBoxes} style={styles.statIcon} />
                    <span style={styles.statCardTitle}>Total Products</span>
                  </div>
                  <div style={styles.statCardValue}>
                    {inventory.totalProducts}
                  </div>
                  <div style={styles.statCardSubtext}>
                    {inventory.lowStockItems} low stock
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      style={styles.statIcon}
                    />
                    <span style={styles.statCardTitle}>Stock Status</span>
                  </div>
                  <div style={styles.statCardValue}>
                    {inventory.outOfStockItems}
                  </div>
                  <div style={styles.statCardSubtext}>Out of stock items</div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon icon={faTags} style={styles.statIcon} />
                    <span style={styles.statCardTitle}>Inventory Value</span>
                  </div>
                  <div style={styles.statCardValue}>
                    GH₵{inventory.totalStockValue.toFixed(2)}
                  </div>
                  <div style={styles.statCardSubtext}>
                    Cost: GH₵{inventory.totalCostValue.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* STOCK MOVEMENTS */}
            <div style={styles.statSection}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faTruck} /> Stock Movements
              </h3>
              <div style={styles.statCards}>
                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon icon={faBoxes} style={styles.statIcon} />
                    <span style={styles.statCardTitle}>Received</span>
                  </div>
                  <div style={styles.statCardValue}>
                    {stockMovementsStats.totalReceived}
                  </div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon
                      icon={faDollarSign}
                      style={styles.statIcon}
                    />
                    <span style={styles.statCardTitle}>Value Change</span>
                  </div>
                  <div style={styles.statCardValue}>
                    GH₵{stockMovementsStats.stockValueChange.toFixed(2)}
                  </div>
                  <div style={styles.statCardSubtext}>Net change</div>
                </div>

                <div style={styles.statCard}>
                  <div style={styles.statCardHeader}>
                    <FontAwesomeIcon icon={faUsers} style={styles.statIcon} />
                    <span style={styles.statCardTitle}>Customers</span>
                  </div>
                  <div style={styles.statCardValue}>
                    {customerStats.totalCustomers}
                  </div>
                  <div style={styles.statCardSubtext}>
                    Active: {customerStats.activeCustomers}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RECENT SALES TABLE */}
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Recent Sales</h3>
            </div>

            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Contact</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!salessliced ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <FontAwesomeIcon icon={faSpinner} spin /> Loading
                        sales...
                      </td>
                    </tr>
                  ) : salessliced.length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        No sales found
                      </td>
                    </tr>
                  ) : (
                    salessliced.map((sale) => {
                      const balanceDisplay = getBalanceDisplay(sale);
                      return (
                        <tr key={sale.id}>
                          <td style={styles.td}>
                            {sale.attributes.field_order_number}
                          </td>
                          <td style={styles.td}>
                            {new Date(
                              sale.attributes.created,
                            ).toLocaleDateString()}
                          </td>
                          <td style={styles.td}>{getCustomerName(sale)}</td>
                          <td style={styles.td}>
                            {getCustomerPhoneNumber(sale)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_amount,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            GH₵
                            {parseFloat(
                              sale.attributes.field_total_paid || 0,
                            ).toFixed(2)}
                          </td>
                          <td style={styles.td}>
                            <span
                              style={{
                                color: balanceDisplay.color,
                                fontWeight: "bold",
                                fontSize: "14px",
                              }}
                            >
                              {balanceDisplay.formatted}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <button
                                style={styles.viewButton}
                                onClick={() =>
                                  stateNavigator.navigate(
                                    "viewsalesinvoicedetails",
                                    {
                                      salesId: sale.id,
                                    },
                                  )
                                }
                                title="View Invoice"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                style={styles.editButton}
                                onClick={() =>
                                  stateNavigator.navigate("editsalesorder", {
                                    salesId: sale.id,
                                  })
                                }
                                title="Edit Sale"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                style={styles.deleteButton}
                                onClick={() => handleDelete(sale.id)}
                                title="Delete Sale"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ALERTS SECTION */}
          {(inventory.lowStockItems > 0 || inventory.outOfStockItems > 0) && (
            <div style={styles.alertsCard}>
              <h3 style={styles.sectionTitle}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> Alerts
              </h3>
              <div style={styles.alertsGrid}>
                {inventory.outOfStockItems > 0 && (
                  <div style={styles.alertItem}>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      style={{ color: "#d32f2f" }}
                    />
                    <span>
                      {inventory.outOfStockItems} products are out of stock
                    </span>
                  </div>
                )}
                {inventory.lowStockItems > 0 && (
                  <div style={styles.alertItem}>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      style={{ color: "#ff9800" }}
                    />
                    <span>
                      {inventory.lowStockItems} products are low on stock
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message="Sale deleted successfully!"
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faFilter}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faFilter}
        />
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  card: {
    background: "#fff",
    padding: "25px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    width: "100%",
    marginTop: "20px",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  alertsCard: {
    background: "#fff3cd",
    padding: "20px",
    borderRadius: "12px",
    marginTop: "20px",
    border: "1px solid #ffeaa7",
  },
  periodSelector: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    padding: "15px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  periodButtons: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  periodButton: {
    padding: "8px 16px",
    borderRadius: "20px",
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:hover": {
      background: "#f8f9fa",
    },
  },
  activePeriodButton: {
    background: "#007bff",
    color: "white",
    borderColor: "#007bff",
  },
  dateRangeDisplay: {
    color: "#666",
    fontSize: "14px",
    fontWeight: "500",
  },
  quickActions: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "25px",
  },
  quickActionButton: {
    background: "white",
    border: "1px solid #e0e0e0",
    borderLeft: "4px solid #007bff",
    padding: "15px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      background: "#f8f9fa",
    },
  },
  quickActionIcon: {
    fontSize: "20px",
    color: "#007bff",
  },
  statisticsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    marginBottom: "20px",
  },
  statSection: {
    background: "white",
    padding: "20px",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  sectionTitle: {
    margin: "0 0 15px 0",
    color: "#333",
    fontSize: "18px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  statCards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "15px",
  },
  statCard: {
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    background: "#f8f9fa",
    transition: "transform 0.2s",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    },
  },
  statCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  statCardTitle: {
    fontSize: "14px",
    color: "#666",
    fontWeight: "500",
  },
  statCardValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
    marginBottom: "5px",
  },
  statCardSubtext: {
    fontSize: "12px",
    color: "#888",
  },
  statIcon: {
    fontSize: "18px",
    color: "#007bff",
  },
  tableContainer: {
    overflowX: "auto",
    marginTop: "10px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  th: {
    background: "#f8f9fa",
    padding: "14px",
    textAlign: "left",
    borderBottom: "2px solid #dee2e6",
    fontWeight: "600",
    color: "#333",
    fontSize: "14px",
  },
  td: {
    padding: "14px",
    borderBottom: "1px solid #eee",
    fontSize: "14px",
  },
  filterBar: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  filterSelect: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #ddd",
    background: "white",
    fontSize: "14px",
    minWidth: "180px",
  },
  filterButton: {
    background: "#6c757d",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    transition: "background 0.2s",
    "&:hover": {
      background: "#5a6268",
    },
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  viewButton: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#bbdefb",
    },
  },
  editButton: {
    backgroundColor: "#e8f5e9",
    color: "#388e3c",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#c8e6c9",
    },
  },
  deleteButton: {
    backgroundColor: "#ffebee",
    color: "#d32f2f",
    border: "none",
    borderRadius: "4px",
    padding: "6px 10px",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.2s",
    "&:hover": {
      backgroundColor: "#ffcdd2",
    },
  },
  alertsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  alertItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px",
    background: "white",
    borderRadius: "6px",
    border: "1px solid #ddd",
  },
};
